import { ClassNames } from "@emotion/react";
import type { Instance } from "@popperjs/core";
import { useMountEffect } from "@react-hookz/web";
import first from "lodash/first";
import { type FunctionComponent } from "react";

import { useC2pa } from "./hooks";
import {
  Container,
  MediaContainer,
  PopoverCss,
  SectionData,
  ViewMoreButton,
  ViewMoreContainer,
} from "./styles";

export type Props = {
  className?: string;
  source: string;
};
export const StarlingImagePopover: FunctionComponent<Props> = ({ source }) => {
  const c2paData = useC2pa(source);

  useMountEffect(() => {
    // Not able to use c2pa-wc without c2pa
    void import("@contentauth/web-components/dist/components/Popover");
    void import("@contentauth/web-components/dist/components/Icon");
    void import("@contentauth/web-components/dist/components/Indicator");
    void import("@contentauth/web-components/dist/components/panels/DiscretePanel");
    void import("@contentauth/web-components/dist/components/panels/sections/PanelSection");
  });

  if (!c2paData) return null;
  const generalSectionData = [
    {
      children: (
        <SectionData>
          <div>{c2paData.producedBy.producer}</div>
          <a href={c2paData.producedBy.organization} rel="noreferrer" target="_blank">
            {c2paData.producedBy.name}
          </a>
        </SectionData>
      ),
      data: c2paData.producedBy.producer,
      header: "Produced By",
      helpText: "Chosen name of the person who exported this content",
    },
    {
      data: c2paData.gpsLocation,
      header: "Location",
    },
    {
      data: c2paData.capturedOn,
      header: "Captured On",
    },
    {
      data: c2paData.capturedWith,
      header: "Captured with",
    },
    {
      children: (
        <SectionData>
          <a
            href={`https://ipfs.io/ipfs/${c2paData.ipfsContentId}`}
            rel="noreferrer"
            target="_blank"
          >
            {c2paData.ipfsContentId}
          </a>
        </SectionData>
      ),
      data: c2paData.ipfsContentId,
      header: "IPFS Content ID",
    },
    {
      children: (
        <SectionData>
          <a
            href={`https://filecoin.tools/${c2paData.fileCoin.id}`}
            rel="noreferrer"
            target="_blank"
          >
            {c2paData.fileCoin.id}
          </a>
        </SectionData>
      ),
      data: c2paData.fileCoin.id,
      header: "FileCoin Piece Content ID",
    },
    {
      data: c2paData.fileCoin.provider,
      header: "Stored With",
    },
    {
      children: (
        <SectionData>
          <a
            href={`https://flowscan.org/transaction/${c2paData.flowNft.id}`}
            rel="noreferrer"
            target="_blank"
          >
            {c2paData.flowNft.id}
          </a>
          <a href={c2paData.flowNft.url} rel="noreferrer" target="_blank">
            View metadata
          </a>
        </SectionData>
      ),
      data: c2paData.flowNft.id,
      header: "Flow NFT",
    },
    {
      children: (
        <SectionData>
          {c2paData.medias.map((socialMedia, index) => (
            <MediaContainer key={index}>
              <cai-icon source={socialMedia["@id"]} />
              <a href={socialMedia["@id"]} rel="noreferrer" target="_blank">
                @{socialMedia.name}
              </a>
            </MediaContainer>
          ))}
        </SectionData>
      ),
      data: first(c2paData.medias)?.name,
      header: "Social media",
      helpText: "Social media accounts connected to the producer of this content",
    },
  ];
  return (
    <ClassNames>
      {({ css }) => (
        <cai-popover
          class={PopoverCss(css)}
          interactive={true}
          placement="left"
          ref={(element?: { popperInstance: Instance }) => {
            void element?.popperInstance?.setOptions({
              modifiers: [
                {
                  enabled: false,
                  name: "preventOverflow",
                },
              ],
            });
          }}
        >
          <cai-indicator slot="trigger" />
          <cai-discrete-panel claim={JSON.stringify(c2paData.claims?.data)} slot="content">
            <Container slot="sections">
              {generalSectionData.map(
                (section, index) =>
                  section.data && (
                    <cai-panel-section
                      header={section.header}
                      helpText={section.helpText}
                      key={index}
                    >
                      {section.children ?? <SectionData>{section.data}</SectionData>}
                    </cai-panel-section>
                  ),
              )}
              <ViewMoreContainer>
                <ViewMoreButton href={c2paData.viewMoreUrl} target="_blank">
                  View More
                </ViewMoreButton>
              </ViewMoreContainer>
            </Container>
          </cai-discrete-panel>
        </cai-popover>
      )}
    </ClassNames>
  );
};
